import { useEffect, useMemo, useState } from "react";
import FormDialog, { FormDialogCancel } from "./FormDialog";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const todayISO = () => new Date().toISOString().slice(0, 10);

const blank = () => ({
    txn_date: todayISO(),
    txn_type: "Expense",
    payment_mode: "UPI",
    amount: "",
    reference_no: "",
    category: "Office Expense",
    counterparty: "",
    remarks: "",
});

/**
 * BankTransactionDialog — create or edit a single bank transaction.
 *
 * Props:
 *   open, onOpenChange      — Dialog visibility (controlled)
 *   mode                    — "create" | "edit"
 *   meta                    — { txn_types, payment_modes, categories } from /meta
 *   existing                — when mode="edit", the existing txn doc
 *   onSaved                 — callback fired after a successful save
 */
export default function BankTransactionDialog({
    open, onOpenChange, mode = "create", meta, existing, onSaved,
}) {
    const [form, setForm] = useState(blank());
    const [busy, setBusy] = useState(false);
    const isEdit = mode === "edit";

    // Snapshot of the initial values to detect dirty
    const initial = useMemo(() => {
        if (isEdit && existing) {
            return {
                txn_date: existing.txn_date || todayISO(),
                txn_type: existing.txn_type || "Expense",
                payment_mode: existing.payment_mode || "UPI",
                amount: existing.amount != null ? String(existing.amount) : "",
                reference_no: existing.reference_no || "",
                category: existing.category || "Office Expense",
                counterparty: existing.counterparty || "",
                remarks: existing.remarks || "",
            };
        }
        return blank();
    }, [isEdit, existing]);

    useEffect(() => {
        if (open) setForm(initial);
    }, [open, initial]);

    const isDirty = useMemo(() => {
        return Object.keys(initial).some((k) => String(form[k] ?? "") !== String(initial[k] ?? ""));
    }, [form, initial]);

    const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

    const submit = async (e) => {
        e?.preventDefault?.();
        if (busy) return;
        const amountNum = Number(form.amount);
        if (!form.txn_date || !form.reference_no.trim() || !form.counterparty.trim()) {
            toast.error("Date, Reference No and Counterparty are mandatory");
            return;
        }
        if (!amountNum || amountNum <= 0) {
            toast.error("Amount must be greater than 0");
            return;
        }
        setBusy(true);
        try {
            const payload = {
                txn_date: form.txn_date,
                txn_type: form.txn_type,
                payment_mode: form.payment_mode,
                amount: amountNum,
                reference_no: form.reference_no.trim(),
                category: form.category,
                counterparty: form.counterparty.trim(),
                remarks: form.remarks.trim() || null,
            };
            if (isEdit && existing) {
                await api.patch(`/billing/bank-transactions/${existing.id}`, payload);
                toast.success("Transaction updated");
            } else {
                await api.post("/billing/bank-transactions", payload);
                toast.success("Transaction recorded");
            }
            setForm(blank());
            onSaved?.();
            onOpenChange(false);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to save transaction"));
        } finally {
            setBusy(false);
        }
    };

    const types = meta?.txn_types || [];
    const modes = meta?.payment_modes || [];
    const categories = meta?.categories || [];

    return (
        <FormDialog
            open={open}
            onOpenChange={onOpenChange}
            isDirty={isDirty}
            contentClassName="max-w-2xl"
            testId="bank-txn-dialog"
            title={isEdit ? "Edit bank transaction" : "Record bank transaction"}
            description={isEdit
                ? "Edits are limited to Super Admin and tracked in the audit log."
                : "Log an operational UPI / bank movement, refund or vendor payment."
            }
        >
            <form onSubmit={submit} className="space-y-4 mt-2" data-testid="bank-txn-form">
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Transaction date *">
                        <input
                            type="date"
                            value={form.txn_date}
                            onChange={(e) => set("txn_date", e.target.value)}
                            data-testid="bank-txn-date"
                            className="w-full h-9 px-3 rounded border border-slate-200 bg-white text-sm focus:outline-none focus:border-indigo-500"
                            required
                        />
                    </Field>
                    <Field label="Type *">
                        <select
                            value={form.txn_type}
                            onChange={(e) => set("txn_type", e.target.value)}
                            data-testid="bank-txn-type"
                            className="w-full h-9 px-3 rounded border border-slate-200 bg-white text-sm focus:outline-none focus:border-indigo-500"
                        >
                            {types.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </Field>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <Field label="Payment mode *">
                        <select
                            value={form.payment_mode}
                            onChange={(e) => set("payment_mode", e.target.value)}
                            data-testid="bank-txn-mode"
                            className="w-full h-9 px-3 rounded border border-slate-200 bg-white text-sm focus:outline-none focus:border-indigo-500"
                        >
                            {modes.map((m) => <option key={m} value={m}>{m}</option>)}
                        </select>
                    </Field>
                    <Field label="Amount (₹) *">
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            inputMode="decimal"
                            value={form.amount}
                            onChange={(e) => set("amount", e.target.value)}
                            data-testid="bank-txn-amount"
                            className="w-full h-9 px-3 rounded border border-slate-200 bg-white text-sm focus:outline-none focus:border-indigo-500"
                            placeholder="0.00"
                            required
                        />
                    </Field>
                </div>

                <Field label="Transaction ID / UTR / UPI Ref No *"
                       hint="Critical for refund tracing and reconciliation.">
                    <input
                        type="text"
                        value={form.reference_no}
                        onChange={(e) => set("reference_no", e.target.value)}
                        data-testid="bank-txn-ref"
                        className="reedos-input font-mono"
                        placeholder="UPI/ICICI/9988776655"
                        required
                    />
                </Field>

                <div className="grid grid-cols-2 gap-3">
                    <Field label="Category *">
                        <select
                            value={form.category}
                            onChange={(e) => set("category", e.target.value)}
                            data-testid="bank-txn-category"
                            className="w-full h-9 px-3 rounded border border-slate-200 bg-white text-sm focus:outline-none focus:border-indigo-500"
                        >
                            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </Field>
                    <Field label="Counterparty / Paid To *">
                        <input
                            type="text"
                            value={form.counterparty}
                            onChange={(e) => set("counterparty", e.target.value)}
                            data-testid="bank-txn-counterparty"
                            className="w-full h-9 px-3 rounded border border-slate-200 bg-white text-sm focus:outline-none focus:border-indigo-500"
                            placeholder="Razorpay / John Doe / Vendor name"
                            required
                        />
                    </Field>
                </div>

                <Field label="Remarks">
                    <textarea
                        rows={2}
                        value={form.remarks}
                        onChange={(e) => set("remarks", e.target.value)}
                        data-testid="bank-txn-remarks"
                        className="w-full px-3 py-2 rounded border border-slate-200 bg-white text-sm focus:outline-none focus:border-indigo-500 resize-none"
                        placeholder="Optional context (invoice month, vendor PO, etc.)"
                    />
                </Field>

                <div className="flex items-center justify-end gap-2 pt-2">
                    <FormDialogCancel disabled={busy} testId="bank-txn-cancel">Cancel</FormDialogCancel>
                    <button
                        type="submit"
                        data-testid="bank-txn-submit"
                        className="px-4 py-2 text-sm font-semibold bg-slate-900 text-white rounded-md hover:bg-slate-800 transition-colors disabled:opacity-60"
                        disabled={busy}
                    >
                        {busy ? "Saving…" : (isEdit ? "Save changes" : "Record transaction")}
                    </button>
                </div>
            </form>
        </FormDialog>
    );
}

function Field({ label, hint, children }) {
    return (
        <label className="block">
            <span className="reedos-eyebrow block mb-1">{label}</span>
            {children}
            {hint && <span className="block text-[11px] text-slate-500 mt-1">{hint}</span>}
        </label>
    );
}
